import React, { FC, useEffect, useRef } from 'react';
import Header from './Header';
import Button from './Button';
import Form from './Form';
import Footer from './Footer';
import Accordion from './Accordion';

import { FormattedMessage } from 'react-intl';
import '../styles/App.scss';
import translate from '../i18n/translate';

import research from '../assets/images/research.png';
import contract from '../assets/images/contract.png';
import database_1 from '../assets/images/database_1.png';
import hosting from '../assets/images/hosting.png';
import launch from '../assets/images/launch.png';
import hotline from '../assets/images/hotline.png';
import scalability from '../assets/images/scalability.png';
import interface_1 from '../assets/images/interface.png';
import rocket from '../assets/images/rocket-launch.png';
import time from '../assets/images/time-sheet.png';
import payment from '../assets/images/secure-payment.png';
import analytics from '../assets/images/analytics.png';

import { LOCALES } from '../i18n';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

interface ILocaleProps {
    name: string;
    locale: string;
}

interface FrontPageProps {
    currentLocale: string;
    languageChange: (lang: string) => void;
}

const FrontPage: FC<FrontPageProps> = ({ currentLocale, languageChange }) => {

    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (!['/ru', '/en'].includes(location.pathname)) {
            navigate('/' + currentLocale);
            return;
        }

        if (params.lang && currentLocale !== params.lang) {
            languageChange(params.lang);
        }
    });

    useEffect(() => {
        document.documentElement.lang = currentLocale;
    }, [currentLocale]);

    const languages: ILocaleProps[] = [
        { name: 'Русский', locale: LOCALES.RUSSIAN },
        { name: 'English', locale: LOCALES.ENGLISH }
    ];

    const myRef = useRef<HTMLDivElement>(null);
    const scrollToRef = (ref: any) => {
        ref.current.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    };

    const executeScroll = () => {
        scrollToRef(myRef);
        myRef.current?.querySelector<HTMLInputElement>('#feedback-name')?.focus({ preventScroll: true });
    };

    return (
        <div>
            <a className='skip-link' href='#main-content'>{currentLocale === 'en' ? 'Skip to content' : 'Перейти к содержимому'}</a>
            <main id='main-content' tabIndex={-1}>
                <section className='main_sec'>
                    <div className='switcher'>
                        <label htmlFor='site-language'>{currentLocale === 'en' ? 'Language' : 'Язык'}</label>
                        <select id='site-language' value={currentLocale} onChange={(locale) => {
                            languageChange(locale.target.value);
                            navigate('/' + locale.target.value);
                        }}>
                            {languages.map(({ name, locale }) => (
                                <option key={locale} value={locale}>
                                    {name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <Header/>
                    <div className='main_sec-wrapper'>
                        <div className='container'>
                            <div className='cover_outside'>
                                <div className='left'>
                                    <h1><FormattedMessage id='headerTitle'/></h1>
                                    <p className='name'>AxiLink</p>
                                    <span><FormattedMessage id='headerSubtitle'/></span>
                                    <span><b>{translate('firstStep')}</b>{translate('takingObligations')}</span>
                                    <Button onClick={executeScroll}>{translate('getDemo')}</Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className='how'>
                    <div className='container'>
                        <h2><FormattedMessage id='how'/></h2>
                        <div className='cover_outside'>
                            <div className='step_block'>
                                <div className='img_block'><img src={research} alt=''/></div>
                                <div className='info_block _half-width'>{translate('strongProductSides')}</div>
                            </div>
                            <div className='step_block'>
                                <div className='img_block'><img src={contract} alt=''/></div>
                                <div className='info_block _half-width'>{translate('formatsAgree')}</div>
                            </div>
                            <div className='step_block'>
                                <div className='img_block'><img src={database_1} alt=''/></div>
                                <div className='info_block'>{translate('implementSystem')}</div>
                            </div>
                            <div className='step_block'>
                                <div className='img_block'><img src={hosting} alt=''/></div>
                                <div className='info_block'>{translate('connectSources')}</div>
                            </div>
                            <div className='step_block'>
                                <div className='img_block'><img src={launch} alt=''/></div>
                                <div className='info_block'>{translate('startProject')}</div>
                            </div>
                            <div className='step_block'>
                                <div className='img_block'><img src={hotline} alt=''/></div>
                                <div className='info_block'>{translate('arrangeSupport')}</div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className='what_is'>
                    <div className='container'>
                        <h2>{translate('aboutCompany')}</h2>
                        <p>{translate('companyInfo')}</p>
                        <p style={{ paddingTop: '1rem' }}>{translate('companyMission')}</p>
                        <p className='bold'>{translate('companyExperience')}</p>
                    </div>
                </section>

                <section className='what_is'>
                    <div className='container'>
                        <h2>{translate('whatIs')}<b>{translate('axiLink')}{translate('questionMark')}</b></h2>
                        <p>{translate('axiLinkDescMain')}</p>
                        <p className='bold'>{translate('manageRequests')}</p>
                    </div>
                </section>

                <section className='decision'>
                    <div className='container'>
                        <p className='decision__axilink-text'>{translate('axiLinkDesc')}.</p>
                        <div className='decision__functionality'>
                            <ul className='dashed'>
                                <li>{translate('fn_collecting')}</li>
                                <li>{translate('fn_support')}</li>
                                <li>{translate('fn_transformation')}</li>
                                <li>{translate('fn_depersonalization')}</li>
                                <li>{translate('fn_cashing')}</li>
                                <li>{translate('fn_parameterization')}</li>
                                <li>{translate('fn_storageAndLogging')}</li>
                            </ul>
                        </div>

                        <p className='top'><b>{translate('noChaos')}</b>{translate('withTuning')}</p>
                        <div className='setting-list'>
                            <div className='setting_block'>
                                <p><b>{translate('single')}</b>{translate('reqFormat')}</p>
                            </div>
                            <div className='setting_block'>
                                <p><b>{translate('readyIntegration')}</b>{translate('analyticalSystems')}</p>
                            </div>
                            <div className='setting_block'>
                                <p><b>{translate('support')}</b>{translate('majorProtocols')}</p>
                            </div>
                            <div className='setting_block'>
                                <p><b>{translate('viewPossibility')}</b>{translate('crmData')}</p>
                            </div>
                        </div>
                        <h2>{translate('whoIs')}<b>{translate('solutionFor')}</b></h2>
                        <p className='bott'>{translate('clients')}</p>
                    </div>
                </section>

                <section className='our_vision'>
                    <div className='container'>
                        <h3>{translate('ourSolution')}</h3>
                        <div className='info_text'>
                            <p className='title'>{translate('it')}</p>
                            <p>{translate('businessFocus')}</p>
                        </div>
                        <div className='info_text'>
                            <p className='title'>{translate('riskTechnologies')}</p>
                            <p>{translate('automatedChecks')}</p>
                        </div>
                        <div className='info_text'>
                            <p className='title'>{translate('business')}</p>
                            <p>{translate('fastStartGuarantee')}</p>
                        </div>
                    </div>
                </section>

                <section className='why_us'>
                    <div className='container'>
                        <h2><b>{translate('why')}</b>{translate('axiLink')}{translate('questionMark')}</h2>
                        <div className='cover_outside'>
                            <div className='cover_inside'>
                                <div className='img_block'>
                                    <img src={scalability} alt=''/>
                                </div>
                                <div className='info_block'>
                                    <p>{translate('scalability')}</p>
                                    <span>{translate('requestProcessing')}</span>
                                </div>
                            </div>
                            <div className='cover_inside'>
                                <div className='img_block'>
                                    <img src={interface_1} alt=''/>
                                </div>
                                <div className='info_block'>
                                    <p>{translate('versatility')}</p>
                                    <span>{translate('allInfoSystems')}</span>
                                </div>
                            </div>
                            <div className='cover_inside'>
                                <div className='img_block'>
                                    <img src={rocket} alt=''/>
                                </div>
                                <div className='info_block'>
                                    <p>{translate('speed')}</p>
                                    <span>{translate('integrationSpeed')}</span>
                                </div>
                            </div>
                            <div className='cover_inside'>
                                <div className='img_block'>
                                    <img src={time} alt=''/>
                                </div>
                                <div className='info_block'>
                                    <p>{translate('saving')}</p>
                                    <span>{translate('costReduction')}</span>
                                </div>
                            </div>
                            <div className='cover_inside'>
                                <div className='img_block'>
                                    <img src={payment} alt=''/>
                                </div>
                                <div className='info_block'>
                                    <p>{translate('security')}</p>
                                    <span>{translate('integrationSecurity')}</span>
                                </div>
                            </div>
                            <div className='cover_inside'>
                                <div className='img_block'>
                                    <img src={analytics} alt=''/>
                                </div>
                                <div className='info_block'>
                                    <p>{translate('fineTuning')}</p>
                                    <span>{translate('requestManagement')}</span>
                                </div>
                            </div>
                        </div>
                        <p className='end_title'>{translate('dateStore')}</p>
                    </div>
                </section>

                <section className='list'>
                    <div className='container'>
                        <h2>{translate('externalDataProvidersList')}</h2>
                        <Accordion/>
                    </div>
                </section>
                <section className='demo'>
                    <div className='container _flex'>
                        <div className='demo-prices'>
                            <div className='demo-prices__order'>
                                <span>{translate('connectionAndService')}</span>
                            </div>
                            <div className='demo-prices__desc'>
                                <span>{translate('connectionAndServiceCostInd')}</span>
                            </div>
                            <div className='demo-prices__desc'>
                                <span>{translate('connectionAndServiceMonthly')}</span>
                            </div>
                            <div className='demo-prices__desc' style={{ paddingTop: '1rem' }}>
                                <span>{translate('onPremise')}</span>
                            </div>
                        </div>
                        {/*<div className='demo-prices'>*/}
                        {/*    <div className='demo-prices__order'>*/}
                        {/*        <span>от 5 000 &#8381;/{translate('month')}</span>*/}
                        {/*    </div>*/}
                        {/*    <div className='demo-prices__desc'>*/}
                        {/*        <span>{translate('service')}</span>*/}
                        {/*    </div>*/}
                        {/*</div>*/}
                        {/*<Button onClick={executeScroll}>{translate('getDemo')}</Button>*/}
                    </div>
                </section>

                <div ref={myRef}>
                    <Form/>
                </div>
            </main>
            <Footer/>
        </div>
    );
};

export default FrontPage;
